import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Tooltip } from '@mui/material';
import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomAutocomplete from '../../../components/CustomAutocomplete';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, ErrorCircleButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import CustomInput from './../../../components/CustomInput';
import CustomSelect from './../../../components/CustomSelect';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import { addEventLogBulk } from '../../../axios/Eventlog/EventLogApi';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { DateTime } from "luxon";
import { useAuth } from '../../../context/AuthContext';
import { useLoading } from '../../../context/LoadingContext';
import { getListUserGroup } from '../../../axios/Group/GroupUrlApi';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getShiftByUser } from '../../../axios/Shift/ShiftApi';
import DeleteIcon from "@mui/icons-material/Delete";

dayjs.extend(utc);
dayjs.extend(timezone);

const normalizeTime = (time) => {
   if (!time) return null;

   if (/^\d{2}:\d{2}$/.test(time)) {
      return `${time}:00`;
   }

   if (/^\d{2}:\d{2}:\d{2}$/.test(time)) {
      return time;
   }

   return null;
};

const buildItemDateTimeValue = (selectDate, selectTime) => {
   if (!selectDate) return null;

   if (!selectTime) return selectDate;

   return dayjs(
      `${dayjs(selectDate).format('YYYY-MM-DD')} ${normalizeTime(selectTime) || selectTime}`
   ).toDate();
};

const EventDateTimeField = memo(({
   selectDate,
   selectTime,
   dateTimeFormat,
   onDateTimeChange,
}) => (
   <CustomDateTimePicker
      mode="singleTime"
      value={buildItemDateTimeValue(selectDate, selectTime)}
      dateTimeFormat={dateTimeFormat}
      onChange={(newDateTime) => {
         const d = dayjs(newDateTime);
         if (!d.isValid()) return;

         onDateTimeChange(d.toDate(), d.format('HH:mm:ss'));
      }}
   />
), (prev, next) =>
   prev.selectDate === next.selectDate &&
   prev.selectTime === next.selectTime &&
   prev.dateTimeFormat === next.dateTimeFormat
);

const AddEventDialog = ({ open, onClose, usersData, shiftsData, onRefresh, selectedDate }) => {
   const { profile, dateTimeFormat, genaralSettingTimeZone } = useAuth();
   const [isFormValid, setIsFormValid] = useState(false);
   const { t } = useTranslation();
   const [show, hide] = useLoading();
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [userGroup, setUserGroup] = useState([]);
   const MAX_REASON_LENGTH = 255;
   const shiftFetchSeqRef = useRef({});

   const createEmptyEventItem = () => ({
      eventType: 1,
      selectDate: selectedDate ? new Date(selectedDate) : new Date(),
      selectTime: DateTime.local().toFormat("HH:mm:ss"),
      shift: null,
      shiftOptions: [],   // ✅ thêm cái này
      shiftDate: new Date(),
   });

   const [eventItems, setEventItems] = useState([
      createEmptyEventItem()
   ]);

   const initialFormData = {
      userName: null,
      timeZone: DateTime.local().toFormat("ZZ"),
      description: '',
      userId: null,
      user: null
   };
   const [formData, setFormData] = useState(initialFormData);

   const eventDateTimeKey = useMemo(
      () => eventItems
         .map((item) => `${dayjs(item.selectDate).format('YYYY-MM-DD')}_${item.selectTime || ''}`)
         .join('|'),
      [eventItems]
   );

   const handleAddItem = () => {
      setEventItems(prev => [...prev, createEmptyEventItem()]);
   };

   const handleRemoveItem = (index) => {
      setEventItems(prev => prev.filter((_, i) => i !== index));
   };

   const handleItemChange = (index, field, value) => {
      setEventItems(prev => {
         const updated = [...prev];
         updated[index] = {
            ...updated[index],
            [field]: value
         };
         return updated;
      });
   };

   const handleItemDateTimeChange = (index, newDate, newTime) => {
      setEventItems(prev => {
         const updated = [...prev];
         updated[index] = {
            ...updated[index],
            selectDate: newDate,
            selectTime: newTime
         };
         return updated;
      });
   };

   useEffect(() => {
      const fetchData = async () => {
         const payload = {
            group: 1,
            pageNo: 1,
            recordPerPage: 50000,
            langId: localStorage.getItem("i18nextLngId")
         }
         show()
         try {
            const response = await getListUserGroup(payload);
            setUserGroup(response.data.data)

         } catch (error) {
            if (error.response) {
               if (error.response.status !== 401) {
                  toast.error(t(error?.response.data.message));
               }
            } else {
               toast.error(t('common.network-timeout'));
            }
         } finally {
            hide()
         }
      };
      fetchData();

   }, [t]);

   useEffect(() => {
      if (!userGroup.length || !profile) return;

      const currentUser = userGroup.find(
         u => u.id === profile.userId
      );

      if (currentUser) {
         setFormData(prev => ({
            ...prev,
            user: currentUser,
            userName: currentUser.fullName,
            userId: currentUser.id
         }));
      }
   }, [userGroup, profile]);

   const handleCloseDialog = () => {
      onClose();
   };

   const handleInputChange = (e) => {
      const { name, value } = e.target;

      if (name === 'description' && value.length > MAX_REASON_LENGTH) {
         return; // không cập nhật nếu quá 255 ký tự
      }

      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const browserTZ = dayjs.tz.guess();

   const buildAttendanceDateTimeUTC = (date, time) => {
      if (!date || !time || !genaralSettingTimeZone) return null;

      // yyyy-MM-dd theo ngày chọn
      const yyyyMMdd = format(date, "yyyy-MM-dd");

      // Tạo datetime theo timezone công ty
      const companyDateTime = dayjs.tz(
         `${yyyyMMdd} ${time}`,
         "YYYY-MM-DD HH:mm:ss",
         browserTZ
      );

      // Convert về UTC ISO string
      return companyDateTime.utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
   };
   useEffect(() => {
      const isValid = !!formData.userName &&
         formData.userId &&
         eventItems.length > 0 &&
         eventItems.every((item) => item.selectDate && item.selectTime && item.shift);
      setIsFormValid(isValid);
   }, [formData, eventItems]);

   const handleSubmitAddEvents = async () => {

      if (!formData.userId) {
         toast.error(t("common.no-employee"));
         return;
      }

      const dataList = eventItems.map(item => {
         const attendanceDateTime = buildAttendanceDateTimeUTC(
            item.selectDate,
            item.selectTime
         );

         return {
            eventType: Number(item.eventType),
            attendanceDateTime,
            shift: item.shift,
            shiftDate: format(item.shiftDate, "yyyy-MM-dd"),
            useDifferentShiftDate: false,
            description: formData.description?.trim() || "",
            langId: localStorage.getItem("i18nextLngId"),
            latitude: "",
            longitude: "",
            userId: formData.userId
         };
      });

      setIsSubmitting(true);
      try {
         const res = await addEventLogBulk(dataList);

         if (res?.data?.responseCode === 0) {
            toast.success(t("common.add-event-log-successfully"));
            onRefresh?.();
            handleCloseDialog();
         } else {
            toast.error(res?.data?.message || "Error");
         }

      } catch (error) {
         let msg = error?.response?.data?.message || error?.message || "Error";
         toast.error(msg);
      } finally {
         setIsSubmitting(false);
      }
   };

   const fetchShiftByUserAndTime = async (userId, date, time, index) => {
      if (!userId || !date || !time) return;

      const nextSeq = (shiftFetchSeqRef.current[index] || 0) + 1;
      shiftFetchSeqRef.current[index] = nextSeq;

      const dateTimeUTC = buildAttendanceDateTimeUTC(date, time);

      try {
         show();

         const payload = {
            userId,
            dateTime: dateTimeUTC,
            langId: localStorage.getItem("i18nextLngId"),
         };

         const response = await getShiftByUser(payload);
         const shiftList = response.data.data || [];

         // Bỏ qua response cũ nếu user đã đổi ngày/giờ trong lúc chờ API
         if (shiftFetchSeqRef.current[index] !== nextSeq) return;

         setEventItems(prev => {
            const updated = [...prev];
            const currentItem = updated[index];
            if (!currentItem) return prev;

            const currentDateTimeUTC = buildAttendanceDateTimeUTC(
               currentItem.selectDate,
               currentItem.selectTime
            );
            if (currentDateTimeUTC !== dateTimeUTC) return prev;

            updated[index] = {
               ...currentItem,
               shiftOptions: shiftList,
               shift: shiftList[0]?.shiftId || null
            };

            return updated;
         });

      } catch (error) {
         if (shiftFetchSeqRef.current[index] !== nextSeq) return;
         toast.error(t(error?.response?.data));
      } finally {
         if (shiftFetchSeqRef.current[index] === nextSeq) {
            hide();
         }
      }
   };

   useEffect(() => {
      if (!open) {
         shiftFetchSeqRef.current = {};
         return;
      }
      if (!formData.userId) return;

      eventItems.forEach((item, index) => {
         if (item.selectDate && item.selectTime) {
            fetchShiftByUserAndTime(
               formData.userId,
               item.selectDate,
               item.selectTime,
               index
            );
         }
      });
   }, [open, formData.userId, eventDateTimeKey]);

   return (
      <CustomDialog open={open} transitionDuration={300} maxWidth={'xl'} loading={isSubmitting} onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('button.add-event')}</p>
               </div>
               <GrayIconButton onClick={handleCloseDialog} disabled={isSubmitting}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div className='grid grid-cols-12 md:gap-x-10 gap-y-3'>
               <div className='col-span-12 md:col-span-6 mt-4'>
                  <label className='mb-2.5'>
                     {t('common.user-name')}<span className='text-[#EF4444] ml-1'>*</span>
                  </label>
                  <CustomAutocomplete
                     className='min-w-60 !mt-2'
                     name='userName'
                     value={formData.user}
                     onDataChange={(name, value) => {
                        setFormData(prev => ({
                           ...prev,
                           user: value,
                           userName: value,
                           userId: value?.id || null
                        }));
                     }}
                     options={userGroup}
                     dataType='user'
                     freeSolo={true}
                     placeholder={t('common.content-161')}
                  />

               </div>
               <div className="col-span-12">
                  <div className="space-y-4">
                     {eventItems.map((item, index) => (
                        <div
                           key={index}
                           className="
          relative
          bg-white
          border
          rounded-[6px]
          p-5
          shadow-sm
          transition-all
          hover:shadow-md
        "
                        >
                           {/* Remove button */}
                           {eventItems.length > 1 && (
                              <Tooltip title={t("button.delete")} arrow>
                                 <ErrorCircleButton className="!bg-[#FEE2E2] !absolute top-3 right-3" onClick={() => handleRemoveItem(index)}>
                                    <DeleteIcon />
                                 </ErrorCircleButton>
                              </Tooltip>
                           )}

                           {/* Title */}
                           <div className="mb-4">
                              <p className="font-semibold text-gray-700">
                                 {t("common.events")} #{index + 1}
                              </p>
                           </div>

                           {/* Event Type */}
                           <div className="mb-4">
                              <RadioGroup
                                 row
                                 value={item.eventType}
                                 onChange={(e) =>
                                    handleItemChange(index, "eventType", Number(e.target.value))
                                 }
                              >
                                 <FormControlLabel
                                    value={1}
                                    control={<Radio size="small" />}
                                    label={t("attendance.CHECK_IN")}
                                 />
                                 <FormControlLabel
                                    value={2}
                                    control={<Radio size="small" />}
                                    label={t("attendance.CHECK_OUT")}
                                 />
                              </RadioGroup>
                           </div>

                           {/* Date-Time / Shift in 1 row */}
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                 <label>{t('common.date-time')}</label><span className='text-[#EF4444] ml-1'>*</span>
                                 <div className='mt-2'>
                                    <EventDateTimeField
                                       selectDate={item.selectDate}
                                       selectTime={item.selectTime}
                                       dateTimeFormat={dateTimeFormat}
                                       onDateTimeChange={(newDate, newTime) =>
                                          handleItemDateTimeChange(index, newDate, newTime)
                                       }
                                    />
                                 </div>
                              </div>

                              <div>
                                 <label>{t('common.shift')}</label><span className='text-[#EF4444] ml-1'>*</span>
                                 <CustomSelect
                                 value={item.shift}
                                 onChange={(e) =>
                                    handleItemChange(index, "shift", e.target.value)
                                 }
                                 options={[...(item.shiftOptions || [])].sort((a, b) => b.shiftId - a.shiftId)}
                                 dataType="shift"
                                 className='mt-2'
                              />
                              </div>
                           </div>
                        </div>
                     ))}
                  </div>
                  <div className="flex items-center justify-end mt-4">
                     <Tooltip title={t("button.add")} arrow>
                        <ControlpanelPrimaryButton
                           onClick={handleAddItem}
                           className="!px-3 !py-1.5"
                        >
                           <AddIcon fontSize="small" />
                           {/* <span className="ml-1">{t("button.add-event")}</span> */}
                        </ControlpanelPrimaryButton>
                     </Tooltip>

                  </div>
               </div>

               <div className='col-span-12 md:col-span-12'>
                  <label>{t('common.description')}</label>
                  <CustomInput
                     name='description'
                     inputType='text'
                     value={formData.description}
                     handleInputChange={handleInputChange}
                     labelEnable={false}
                     className='mt-2'
                     placeholder={t('common.description')}
                  />

                  {/* <div
                     className={`flex justify-end text-xs mt-1 ${formData.description.length >= MAX_REASON_LENGTH
                        ? "text-red-500"
                        : "text-gray-500"
                        }`}
                  >
                     {formData.description?.length || 0}/{MAX_REASON_LENGTH}
                  </div> */}
               </div>
            </div>

         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog} disabled={isSubmitting}>
                  <p>{t('button.close')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton onClick={(e) => {
                  handleSubmitAddEvents()
               }}
                  disabled={isSubmitting || !isFormValid}
               >
                  <p>{t('button.add-event')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default AddEventDialog;
